import { useContext, useEffect, useState } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import { getDD_Month_Time } from 'utils/date';
import Tab from 'widgets/tab';
import { ModalMobile, ModalDesktop } from '@acko-tech/building-blocks.ui.modal';
import { OrderStatus } from './constants';
import TransactionDetailsModal from './detailsModal';
import TransactionItemCard from './itemCard';
import SnackBar from 'library/snackbar';
import { useResponsiveDevice } from 'hooks/useResponsiveDevice';
import { IOrder } from '../types';
import { getPlanName, whatsCoverText } from 'utils/generics';
import { MPosContext } from 'utils/context';
import { useRouter } from 'next/router';
import { useFetchParams } from 'hooks/useFetchParams';
import { SegmentPages, SegmentTrackEvents } from 'utils/segment/constants';
import { trackEvent } from '@acko-tech/building-blocks.ui.acko-analytics';
import * as Styles from './styles';

enum TabOptions {
    PENDING = 'Pending',
    COMPLETED = 'Completed',
}

interface IProps {
    pendingOrders: IOrder[];
    completedOrders: IOrder[];
    hasMorePending: boolean;
    hasMoreCompleted: boolean;
    onLoadMore: any;
}

function OrderHistory({
    pendingOrders,
    completedOrders,
    hasMorePending,
    hasMoreCompleted,
    onLoadMore,
}: IProps) {
    const { lob } = useFetchParams();
    const router = useRouter();
    const { isMobile } = useResponsiveDevice();
    const { partnerId, dealerType } = useContext(MPosContext);

    const [selectedOrder, setSelectedOrder] = useState<IOrder | null>(null);
    const [selectedTab, setSelectedTab] = useState(TabOptions.PENDING);
    const [modalStatus, setModalStatus] = useState(false);
    const [isBtnLoading, setIsBtnLoading] = useState(false);
    const [snackbar, setSnackbar] = useState({
        type: '',
        message: '',
    });

    useEffect(() => {
        const eventName =
            selectedTab === TabOptions.PENDING
                ? SegmentTrackEvents.TAP_CTA_PENDING
                : SegmentTrackEvents.TAP_CTA_COMPLETED;
        trackEvent(eventName, {
            page: SegmentPages.DASHBOARD,
            partner: partnerId,
            product: 'pos',
            journey: 'buy',
            platform: 'mweb',
            type: dealerType,
        });
    }, [selectedTab]);

    const onSelectTab = (option: TabOptions) => {
        setSelectedTab(option);
    };

    const getActiveOrdersList = () => {
        if (selectedTab === TabOptions.COMPLETED) {
            return completedOrders;
        }
        return pendingOrders;
    };

    const onClickOrder = (order: IOrder) => {
        setModalStatus(true);
        setSelectedOrder(order);
    };

    const orderStatus = (status: string, statusMessage: string) => {
        switch (status) {
            case OrderStatus.ORDER_CREATED:
                return {
                    text: 'Link sent',
                    color: '#5920C5',
                    background: '#EFE9FB',
                };
            default:
                return {
                    text: statusMessage,
                    color: '#D83D37',
                    background: '#FBEAEA',
                };
        }
    };

    const getValueObjects = (order: IOrder) => {
        if (selectedTab === TabOptions.PENDING) {
            const { text, color, background } = orderStatus(
                order.status,
                order.status_message
            );
            return {
                value1: order.device.name,
                value2: getDD_Month_Time(new Date(order.updated_on)),
                value3: order.customer.phone,
                value4: text,
                labelColor: {
                    color,
                    background,
                },
            };
        }
        return {
            value1: order.customer.name,
            value2: `₹${order.plan.premium}`,
            value3: order.device.name,
            value4: order.device.purchase_date,
        };
    };

    const segmentEvent = (status: 'resend' | 'pay_acko') => {
        try {
            const eventName =
                status === 'resend'
                    ? SegmentTrackEvents.TAP_CTA_RESEND_LINK
                    : SegmentTrackEvents.TAP_CTA_PAY_ACKO;
            trackEvent(eventName, {
                page: SegmentPages.DASHBOARD,
                partner: partnerId,
                product: 'pos',
                journey: 'buy',
                platform: 'mweb',
                type: dealerType,
            });
        } catch {}
    };

    const onClick = (status: 'resend' | 'pay_acko') => {
        if (!selectedOrder) return;
        segmentEvent(status);
        if (status === 'pay_acko') {
            router.push(
                `/${lob}/${partnerId}/payment?order_ekey=${selectedOrder.ekey}`
            );
            return;
        }
        if (navigator.share && partnerId === 'hero_vida') {
            const baseOrigin = window.location.origin;
            let baseUrl = 'https://www.acko.com';
            if (baseOrigin.includes('ackodev'))
                baseUrl = 'https://www.ackodev.com';
            navigator
                .share({
                    title: 'Share payment link',
                    url: `${baseUrl}/mpos/partner/hero_vida?order_ekey=${selectedOrder.ekey}&phone=${selectedOrder.customer.phone}`,
                })
                .then(() => console.log('shared'))
                .catch((error) => console.log('error', error));
            return;
        }
        import('./apis').then(async (req) => {
            setIsBtnLoading(true);
            const { status, statusCode, errorMessage } =
                await req.resendLinkToCustomer(
                    partnerId,
                    selectedOrder?.ekey || ''
                );
            if (statusCode === 401) {
                router.push(`/login`);
                setSnackbar({
                    type: 'failure',
                    message: 'Token expired. Redirecting to login page.',
                });
                return;
            }
            setIsBtnLoading(false);
            if (status) {
                setSnackbar({
                    type: 'success',
                    message: 'Payment link has sent successfully.',
                });
                return;
            }
            setSnackbar({
                type: 'failure',
                message: errorMessage || 'Oops. Something went wrong.',
            });
        });
    };

    const Modal: any = isMobile ? ModalMobile : ModalDesktop;

    return (
        <Styles.Wrapper>
            <Styles.AllTransactionText>
                All transactions
            </Styles.AllTransactionText>
            <Styles.TabWrapper>
                <Tab
                    items={[TabOptions.PENDING, TabOptions.COMPLETED]}
                    color={'#0FA457'}
                    selectedTab={selectedTab}
                    onSelectTab={onSelectTab}
                />
            </Styles.TabWrapper>
            <div>
                {getActiveOrdersList().length === 0 ? (
                    <Styles.NoResult>No transactions</Styles.NoResult>
                ) : (
                    <InfiniteScroll
                        dataLength={getActiveOrdersList().length}
                        next={() => onLoadMore(selectedTab.toLowerCase())}
                        hasMore={
                            selectedTab === TabOptions.PENDING
                                ? hasMorePending
                                : hasMoreCompleted
                        }
                        loader={<></>}
                    >
                        {getActiveOrdersList().map((order, index) => (
                            <Styles.ItemCardWrapper
                                key={order.ekey}
                                onClick={() => onClickOrder(order)}
                            >
                                <TransactionItemCard
                                    {...getValueObjects(order)}
                                />
                                {index !== getActiveOrdersList().length - 1 && (
                                    <Styles.Hr />
                                )}
                            </Styles.ItemCardWrapper>
                        ))}
                    </InfiniteScroll>
                )}
            </div>
            <Modal
                header={<></>}
                show={modalStatus}
                onHide={() => setModalStatus(false)}
                padding={'20px'}
                wrapperStyles={`border-radius: 16px 16px 0px 0px;`}
                showOverlayBackground={true}
            >
                {selectedOrder && (
                    <TransactionDetailsModal
                        selectedTab={selectedTab}
                        deviceName={selectedOrder?.device?.name}
                        lastUpdatedAt={selectedOrder?.updated_on}
                        status={selectedOrder?.status}
                        statusMessage={selectedOrder?.status_message}
                        serialNumber={selectedOrder?.device?.serial_number}
                        planName={getPlanName(selectedOrder?.plan.plan_name)}
                        mobileNumber={selectedOrder.customer.phone}
                        amount={selectedOrder.plan.premium}
                        deviceCategory={selectedOrder.device.category}
                        coverage={whatsCoverText(
                            selectedOrder.plan.plan_category?.toLowerCase()
                        )}
                        onClick={onClick}
                        isBtnLoading={isBtnLoading}
                        vin={selectedOrder?.device?.vin}
                    />
                )}
            </Modal>
            <SnackBar
                {...snackbar}
                handleSnakbarComplete={() => {
                    setSnackbar({ type: '', message: '' });
                }}
            />
        </Styles.Wrapper>
    );
}

export default OrderHistory;
